import { useState } from 'react';
import { useGT } from 'gt-react';
import { XIcon } from '@phosphor-icons/react';
import {
  MultiSelect,
  type MultiSelectOption,
} from '@/components/common/MultiSelect';
import { pricelistVendorName } from '@/components/company/companyPickerDisplay';
import { cn } from '@/lib/utils';
import {
  pricelistCaseVendorChips,
  pricelistCaseVendorOptionId,
  pricelistCaseVendorsFromOptions,
  removePricelistCaseVendorChip,
  samePricelistCaseVendors,
  type PricelistCaseVendor,
  type PricelistCaseVendorChip,
  type PricelistCaseVendorOption,
} from './pricelistCaseVendors';
import { usePricelistCaseVendorOptions } from './usePricelistCaseVendorOptions';

function chipLabel(chip: PricelistCaseVendorChip): string {
  const name = pricelistVendorName(chip.companyName);
  return chip.vendorNumber === null ? name : `${chip.vendorNumber} — ${name}`;
}

function toMultiSelectOption(
  option: PricelistCaseVendorOption,
): MultiSelectOption {
  return {
    id: pricelistCaseVendorOptionId(option),
    label: chipLabel(option),
    value: option,
  };
}

function isVendorOption(value: unknown): value is PricelistCaseVendorOption {
  return (
    typeof value === 'object' &&
    value !== null &&
    'companyId' in value &&
    typeof value.companyId === 'string' &&
    'companyName' in value &&
    typeof value.companyName === 'string' &&
    'vendorNumber' in value &&
    (value.vendorNumber === null || typeof value.vendorNumber === 'string')
  );
}

function fromMultiSelectOptions(
  selected: readonly MultiSelectOption[],
): PricelistCaseVendor[] {
  const options: PricelistCaseVendorOption[] = [];
  for (const option of selected) {
    const vendorOption: unknown = option.value;
    if (isVendorOption(vendorOption)) options.push(vendorOption);
  }
  return pricelistCaseVendorsFromOptions(options);
}

function VendorChip({
  chip,
  disabled,
  onRemove,
}: {
  chip: PricelistCaseVendorChip;
  disabled: boolean;
  onRemove: () => void;
}) {
  const gt = useGT();
  const label = chipLabel(chip);
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onRemove}
      title={label}
      aria-label={gt('Remove vendor {name}', {
        context:
          'accessible label for removing a vendor chip from a price change',
        name: label,
      })}
      className={cn(
        'group inline-flex max-w-full items-center gap-1 rounded-full border bg-muted/40 py-0.5 pl-2 pr-1.5 text-xs transition-colors',
        'cursor-pointer hover:border-destructive/50 hover:bg-destructive/10',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        'disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:border-border disabled:hover:bg-muted/40',
      )}
    >
      <span className="max-w-[12rem] truncate">{label}</span>
      <XIcon
        aria-hidden
        className="h-3 w-3 shrink-0 text-muted-foreground group-hover:text-destructive group-disabled:group-hover:text-muted-foreground"
      />
    </button>
  );
}

interface Props {
  vendors: readonly PricelistCaseVendor[];
  /** A chip's ×, or the picker closing with a changed selection. */
  onChange: (next: PricelistCaseVendor[]) => void;
  disabled?: boolean;
  /** A saved case keeps at least one vendor; a new one may start empty. */
  keepLastVendor?: boolean;
  triggerPlaceholder: string;
}

/**
 * The vendor set of a price change (GEN-3074), shared by the case sidebar and
 * the create prompt: removable chips — one per picked vendor#
 * ("61500 — THOMAS & BETTS") or per company-level vendor — over the shared
 * `MultiSelect`, fed by the server-side vendor# search.
 *
 * The picker applies once, when it closes (`liveUpdate={false}`), and pins
 * what is ticked above the results, so picks made under one search stay
 * visible and untickable under the next.
 */
export function PricelistCaseVendorSelector({
  vendors,
  onChange,
  disabled = false,
  keepLastVendor = false,
  triggerPlaceholder,
}: Props) {
  const gt = useGT();
  const [search, setSearch] = useState('');
  const results = usePricelistCaseVendorOptions(search);
  const chips = pricelistCaseVendorChips(vendors);
  const value = chips.map(toMultiSelectOption);
  const options = results.map(toMultiSelectOption);

  function change(next: PricelistCaseVendor[]) {
    if (keepLastVendor && next.length === 0) return;
    if (samePricelistCaseVendors(next, vendors)) return;
    onChange(next);
  }

  return (
    <>
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {chips.map((chip) => (
            <VendorChip
              key={pricelistCaseVendorOptionId(chip)}
              chip={chip}
              disabled={disabled || (keepLastVendor && chips.length <= 1)}
              onRemove={() =>
                change(
                  removePricelistCaseVendorChip(vendors, chip, keepLastVendor),
                )
              }
            />
          ))}
        </div>
      )}
      <MultiSelect
        options={options}
        value={value}
        onChange={(selected) => change(fromMultiSelectOptions(selected))}
        onSearchChange={setSearch}
        liveUpdate={false}
        shouldAutoSort={false}
        pinSelected
        showSelectAll={false}
        summaryText={() => triggerPlaceholder}
        placeholder={triggerPlaceholder}
        aria-label={triggerPlaceholder}
        searchPlaceholder={gt(
          'Search by vendor number, company name, or company code...',
          {
            context:
              'vendor search placeholder when adding a vendor to a price change',
          },
        )}
        disabled={disabled}
        size="sm"
        fullWidth
      />
    </>
  );
}
